import React, { useContext } from 'react';
import sanitizeHtml from 'sanitize-html-react';

import { PrimaryButton, H1, H2, AccordionGroup, AccordionItem } from '@zooplus/ui-components';

import FeaturesContext from '../../../common/contexts/FeaturesContext';
import LocResourcesContext from '../../../common/contexts/LocResourcesContext';
import { BonusPointsOverview } from '../../../common/utils/types';
import zeroPointsDesktop from '../../assets/img/zeroZoopointsLP-desktop.jpg';
import zeroPointsDesktopBigger from '../../assets/img/zeroZoopointsLP-desktop@2x.jpg';
import zeroPointsMobile from '../../assets/img/zeroZoopointsLP-mobile.jpg';
import zeroPointsMobileBigger from '../../assets/img/zeroZoopointsLP-mobile@2x.jpg';

import LoyaltyPrograms from './LoyaltyPrograms';
import ProgressBarFirstPhase from './progress-bar/ProgressBarFirstPhase';
import ProgressBarSecondPhase from './progress-bar/ProgressBarSecondPhase';

interface ShutdownViewProps {
  bpEnabled: boolean;
  switchView?: () => void;
  view?: string;
  bonusPointsOverview?: BonusPointsOverview;
  redeemableBalance?: number;
}
const ShutdownView: React.FC<ShutdownViewProps> = ({
  bpEnabled,
  switchView,
  view,
  bonusPointsOverview,
  redeemableBalance,
}) => {
  const GLOBAL_PREFIX: string = 'loy-bp-shutdown';
  const END_DATE: Date = new Date('2024-03-31');
  const { onFormatLocale, onFormatDateString } = useContext(LocResourcesContext);
  const { features } = useContext(FeaturesContext);
  const hasExpiringBalance =
    !!bonusPointsOverview &&
    !!bonusPointsOverview.expiryDate &&
    bonusPointsOverview.expiringBalance &&
    bonusPointsOverview.expiringBalance > 0;
  const firstExpiringDate = hasExpiringBalance
    ? new Date(bonusPointsOverview.expiryDate)
    : undefined;
  const firstExpiringBalance = hasExpiringBalance ? bonusPointsOverview.expiringBalance : undefined;
  const handleClick = () => {
    switchView?.();
  };
  return (
    <>
      <div className={`${GLOBAL_PREFIX}-breadcrumb`}>
        <span className={`${GLOBAL_PREFIX}-breadcrumb--reward`}>
          {onFormatLocale?.('mzp.overview.bonus-program.view-reward-shop')}
        </span>
        <span className={`${GLOBAL_PREFIX}-breadcrumb--goodbye`}>
          {' / ' + onFormatLocale?.('loy.bp.shutdown.breadcrumb')}
        </span>
      </div>
      <div className={`${GLOBAL_PREFIX}-summary`}>
        <picture>
          <source srcSet={zeroPointsDesktopBigger as string} media="(min-width: 768px)" />
          <source srcSet={zeroPointsMobileBigger as string} media="(max-width: 767px)" />
          <source srcSet={zeroPointsDesktop as string} media="(min-width: 768px)" />
          <source srcSet={zeroPointsMobile as string} media="(max-width: 767px)" />
          <img src={zeroPointsDesktop as string} alt="Imagen" className={`${GLOBAL_PREFIX}-summary__image`} />
        </picture>
        <div className={`${GLOBAL_PREFIX}-summary__content`}>
          <H1 className={`${GLOBAL_PREFIX}-summary__content--title`}>
            {onFormatLocale?.('loy.bp.shutdown.title')}
          </H1>
          <p
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(onFormatLocale?.('loy.bp.shutdown.paragraph-1') ?? ''),
            }}
          />
          <p>{onFormatLocale?.('loy.bp.shutdown.paragraph-2')}</p>
          <p>{onFormatLocale?.('loy.bp.shutdown.paragraph-3')}</p>
          {bpEnabled && (
            <PrimaryButton onClick={handleClick} className={`${GLOBAL_PREFIX}-summary__button`}>
              {onFormatLocale?.('loy.bp.shutdown.redeem')}
            </PrimaryButton>
          )}
        </div>
      </div>
      {bpEnabled &&
        redeemableBalance &&
        firstExpiringDate &&
        view === 'private' &&
        redeemableBalance > 0 &&
        hasExpiringBalance &&
        firstExpiringDate <= END_DATE && (
          <div className={`${GLOBAL_PREFIX}-bp`}>
            <H2>{onFormatLocale?.('loy.bp.shutdown.bp.title')}</H2>
            <div className={`${GLOBAL_PREFIX}-bp__balance`}>
              <span className={`${GLOBAL_PREFIX}-bp__balance--current`}>
                <strong className={`${GLOBAL_PREFIX}-bp__balance--value`}>
                  {redeemableBalance + ' '}
                </strong>
                <span className={`${GLOBAL_PREFIX}-bp__balance--description`}>
                  {onFormatLocale?.('loy.points.available')}
                </span>
              </span>
              <span className={`${GLOBAL_PREFIX}-bp__balance--expired`}>
                <strong className={`${GLOBAL_PREFIX}-bp__balance--value`}>
                  {firstExpiringBalance + ' '}
                </strong>
                <span className={`${GLOBAL_PREFIX}-bp__balance--description`}>
                  {onFormatLocale?.('loy.points.expiry') +
                    ' ' +
                    onFormatDateString?.(firstExpiringDate)}
                </span>
              </span>
            </div>
          </div>
        )}
      {bpEnabled && features.shutdownSecondPhase === false && <ProgressBarFirstPhase />}
      {bpEnabled && features.shutdownSecondPhase && <ProgressBarSecondPhase />}
      {!bpEnabled && <LoyaltyPrograms />}
      <H2 style={{ marginTop: '40px' }}>{onFormatLocale?.('loy.bp.shutdown.faqs')}</H2>
      <AccordionGroup allowMultipleOpen>
        <React.Fragment key=".0">
          <AccordionItem title={onFormatLocale?.('loy.bp.shutdown.faq-1') ?? ''}>
            <p
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(onFormatLocale?.('loy.bp.shutdown.faq-1.content') ?? ''),
              }}
            />
          </AccordionItem>
          <AccordionItem title={onFormatLocale?.('loy.bp.shutdown.faq-2') ?? ''}>
            <p
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(onFormatLocale?.('loy.bp.shutdown.faq-2.content') ?? ''),
              }}
            />
          </AccordionItem>
          <AccordionItem title={onFormatLocale?.('loy.bp.shutdown.faq-3') ?? ''}>
            <p
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(onFormatLocale?.('loy.bp.shutdown.faq-3.content') ?? ''),
              }}
            />
          </AccordionItem>
          <AccordionItem title={onFormatLocale?.('loy.bp.shutdown.faq-4') ?? ''}>
            <p
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(onFormatLocale?.('loy.bp.shutdown.faq-4.content') ?? ''),
              }}
            />
          </AccordionItem>
          <AccordionItem title={onFormatLocale?.('loy.bp.shutdown.faq-5') ?? ''}>
            <p
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(onFormatLocale?.('loy.bp.shutdown.faq-5.content') ?? ''),
              }}
            />
          </AccordionItem>
        </React.Fragment>
      </AccordionGroup>
    </>
  );
};

export default ShutdownView;
